/**
 * Enhance Scope v2 (B-531) — the "Enhance" mode root on V2SupplementPage.
 *
 * Slice 1 (deterministic, no LLM). Top: loss-context row (Cat-3 switch,
 * claim-type select, jurisdiction) + overall narrative + collapsible per-room
 * narratives generated from the parsed rooms (the user never types a room
 * name). Narratives autosave (debounced) via saveEnhanceSession — slice 1 does
 * NOT interpret them yet (labelled as such). Below: "Run enhancement" fires the
 * deterministic engine and renders a room-grouped proposal queue; approving +
 * Apply merges provenance=ADDED lines into the persisted parse and replaces
 * page state (WYSIWYG, exactly like the CodeAssigner override flow). Applied
 * proposals collapse with a Revert.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BackendAPI } from '../../../lib/api';
import type {
  ApiError,
  EnhanceContext,
  EnhanceNarratives,
  EnhanceProposal,
  EnhanceResolution,
  EnhanceRun,
  EnhanceSessionResponse,
  ParsedEstimateResponse,
} from '../../../types';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { IconCheck, IconCopy, IconSpark, IconWarn } from '../icons';
import { ProposalCard, type ProposalCardState } from './ProposalCard';
import { ApplyBar } from './ApplyBar';
import { buildSupplementMemo } from './memo';

const HOUSE_KEY = '__house__';
const SAVE_DEBOUNCE_MS = 800;

/** Props for {@link EnhancePanel}. */
export interface EnhancePanelProps {
  result: ParsedEstimateResponse;
  parseHash: string;
  /** Feature-detection result from the page (non-null when this renders). */
  session: EnhanceSessionResponse;
  /** Replace page state with the returned parse after apply/revert. */
  onResult: (_res: ParsedEstimateResponse) => void;
  /** Notify the page so it can re-hydrate the session (keeps applied ids fresh
   *  across mode switches). */
  onSessionChanged?: () => void;
}

/** The narrative-driven enhancement mode (slice 1). */
export function EnhancePanel({
  result,
  parseHash,
  session,
  onResult,
  onSessionChanged,
}: EnhancePanelProps) {
  // Parsed room names — the narrative + room-pick vocabulary. Deduped; the user
  // never types these.
  const rooms = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const r of result.estimate.rooms) {
      const name = r.group_description || r.group_code;
      if (name && !seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    }
    return names;
  }, [result]);

  const [narratives, setNarratives] = useState<EnhanceNarratives>(
    () => session.session?.narratives ?? { overall: '', rooms: {} },
  );
  const [context, setContext] = useState<EnhanceContext>(
    () =>
      session.session?.context ?? {
        cat3_confirmed: false,
        claim_type: null,
        jurisdiction: null,
      },
  );
  const [run, setRun] = useState<EnhanceRun | null>(session.latest_run);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(
    () => new Set(session.applied_proposal_ids),
  );
  const [triage, setTriage] = useState<Record<string, ProposalCardState>>({});
  // Confirm-chip answers, keyed by proposal_id. An answer unlocks approve and
  // (slice 3) is sent to /apply for audit.
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // MODIFY candidate index actually applied, keyed by proposal_id — so an
  // applied MODIFY card / the memo can show the swap the user chose after
  // triage is cleared.
  const [appliedChoices, setAppliedChoices] = useState<Record<string, number>>({});
  const [memoCopied, setMemoCopied] = useState(false);
  const [openRooms, setOpenRooms] = useState<Record<string, boolean>>({});

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  // Set when a mode:"full" run 400s because the server-side LLM flag is off.
  // Recoverable: the UI offers the deterministic run instead.
  const [llmOffError, setLlmOffError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{
    applied: number;
    skipped: Array<{ proposal_id: string; reason: string }>;
  } | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  // --- debounced autosave ------------------------------------------------
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(
    (narr: EnhanceNarratives, ctx: EnhanceContext) => {
      setSaveState('saving');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        BackendAPI.saveEnhanceSession(parseHash, { narratives: narr, context: ctx })
          .then(() => setSaveState('saved'))
          .catch(() => setSaveState('idle'));
      }, SAVE_DEBOUNCE_MS);
    },
    [parseHash],
  );
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const updateOverall = (v: string) => {
    const next = { ...narratives, overall: v };
    setNarratives(next);
    scheduleSave(next, context);
  };
  const updateRoomNarrative = (room: string, v: string) => {
    const next = { ...narratives, rooms: { ...narratives.rooms, [room]: v } };
    setNarratives(next);
    scheduleSave(next, context);
  };
  const updateContext = (patch: Partial<EnhanceContext>) => {
    const next = { ...context, ...patch };
    setContext(next);
    scheduleSave(narratives, next);
  };

  // True when there's any narrative text to interpret — drives whether the
  // primary "Run enhancement" fires a full (LLM) or deterministic run.
  const hasNarrative = useMemo(() => {
    if (narratives.overall.trim()) return true;
    return Object.values(narratives.rooms).some((v) => v.trim());
  }, [narratives]);

  // --- run ---------------------------------------------------------------
  const handleRun = async (mode: 'deterministic' | 'full') => {
    setRunning(true);
    setRunError(null);
    setLlmOffError(null);
    setApplyResult(null);
    try {
      const r = await BackendAPI.runEnhance(parseHash, mode);
      setRun(r);
      setTriage({});
      setAnswers({});
    } catch (e) {
      const status = (e as ApiError | undefined)?.status;
      const msg = e instanceof Error ? e.message : 'Enhancement run failed.';
      // A full run 400s when the server-side LLM flag is off. Treat that as a
      // recoverable state that offers the deterministic run, not a hard error.
      if (mode === 'full' && status === 400) {
        setLlmOffError(msg);
      } else {
        setRunError(msg);
      }
    } finally {
      setRunning(false);
    }
  };

  const setAnswer = (id: string, a: string) =>
    setAnswers((prev) => ({ ...prev, [id]: a }));

  // A blocking confirm chip with no answer yet gates approve for that proposal.
  const isBlocked = useCallback(
    (p: EnhanceProposal) => !!(p.confirm?.blocking && !answers[p.proposal_id]),
    [answers],
  );

  const proposals = useMemo(() => run?.proposals ?? [], [run]);

  // A proposal is approvable (ADD or MODIFY); FLAG is a gate, not an addition.
  const isApprovable = (p: EnhanceProposal) => p.kind === 'ADD' || p.kind === 'MODIFY';
  // Roomless only applies to ADD — a MODIFY carries its room in modify.room.
  const isRoomless = (p: EnhanceProposal) => p.kind === 'ADD' && p.room == null;
  const roomKey = (p: EnhanceProposal) => p.room ?? p.modify?.room ?? HOUSE_KEY;

  // FLAG dependents: map each dependent proposal_id → its gating flag id.
  const depToFlag = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of proposals) {
      if (p.kind === 'FLAG') for (const d of p.dependent_ids) m.set(d, p.proposal_id);
    }
    return m;
  }, [proposals]);

  // A dependent stays hidden until its flag is confirmed. Already-applied
  // dependents are always shown (their flag was confirmed at apply time).
  const isHiddenDependent = useCallback(
    (p: EnhanceProposal) => {
      if (appliedIds.has(p.proposal_id)) return false;
      const flagId = depToFlag.get(p.proposal_id);
      if (!flagId) return false;
      return triage[flagId]?.action !== 'approve';
    },
    [appliedIds, depToFlag, triage],
  );

  const visibleProposals = useMemo(
    () => proposals.filter((p) => !isHiddenDependent(p)),
    [proposals, isHiddenDependent],
  );

  // Grouped by room; roomless proposals go under a leading "House-wide" group.
  const groups = useMemo(() => {
    const map = new Map<string, EnhanceProposal[]>();
    for (const p of visibleProposals) {
      const key = roomKey(p);
      const arr = map.get(key);
      if (arr) arr.push(p);
      else map.set(key, [p]);
    }
    // House-wide first, then rooms in first-seen order.
    const ordered: Array<[string, EnhanceProposal[]]> = [];
    if (map.has(HOUSE_KEY)) ordered.push([HOUSE_KEY, map.get(HOUSE_KEY)!]);
    for (const [k, v] of map) if (k !== HOUSE_KEY) ordered.push([k, v]);
    return ordered;
  }, [visibleProposals]);

  // Visible ADD/MODIFY proposals not already applied are actionable in the tally.
  const actionable = useMemo(
    () => visibleProposals.filter((p) => isApprovable(p) && !appliedIds.has(p.proposal_id)),
    [visibleProposals, appliedIds],
  );
  const approvedCount = actionable.filter(
    (p) => triage[p.proposal_id]?.action === 'approve',
  ).length;
  const rejectedCount = actionable.filter(
    (p) => triage[p.proposal_id]?.action === 'reject',
  ).length;
  const openCount = actionable.length - approvedCount - rejectedCount;

  // Applied proposals (ADD + MODIFY) — the memo's contents.
  const appliedProposals = useMemo(
    () => proposals.filter((p) => appliedIds.has(p.proposal_id)),
    [proposals, appliedIds],
  );

  // Question-free required ADD/MODIFY: required tier, verified, room-anchored,
  // not yet approved. (Roomless / unverified need an answer first → excluded.)
  const requiredApprovable = useMemo(
    () =>
      actionable.filter(
        (p) =>
          p.tier === 'required' &&
          !p.unverified &&
          !isRoomless(p) &&
          !isBlocked(p) &&
          triage[p.proposal_id]?.action !== 'approve',
      ),
    [actionable, triage, isBlocked],
  );

  const approveAllRequired = () => {
    setTriage((prev) => {
      const next = { ...prev };
      for (const p of requiredApprovable) {
        next[p.proposal_id] = { ...next[p.proposal_id], action: 'approve' };
      }
      return next;
    });
  };

  const setProposalState = (id: string, patch: Partial<ProposalCardState>) => {
    setTriage((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  // --- apply -------------------------------------------------------------
  const buildResolutions = (): EnhanceResolution[] => {
    const out: EnhanceResolution[] = [];
    for (const p of actionable) {
      const t = triage[p.proposal_id];
      if (!t?.action) continue;
      const ans = answers[p.proposal_id];
      if (t.action === 'approve') {
        const res: EnhanceResolution = {
          proposal_id: p.proposal_id,
          action: 'approve',
          room: p.room ?? p.modify?.room ?? t.room ?? null,
          quantity: t.quantity ?? p.quantity ?? null,
        };
        // MODIFY: which candidate the user swapped to.
        if (p.kind === 'MODIFY' && p.modify) {
          res.choice_index = t.choiceIndex ?? p.modify.default_index;
        }
        // Confirm-chip answer → audit.
        if (ans) res.answer = ans;
        out.push(res);
      } else {
        const res: EnhanceResolution = { proposal_id: p.proposal_id, action: 'reject' };
        if (ans) res.answer = ans;
        out.push(res);
      }
    }
    // A rejected FLAG auto-rejects its (hidden) dependents so the backend audits
    // the whole decision, not just the flag.
    for (const f of proposals) {
      if (
        f.kind === 'FLAG' &&
        f.dependent_ids.length > 0 &&
        triage[f.proposal_id]?.action === 'reject'
      ) {
        for (const depId of f.dependent_ids) {
          if (appliedIds.has(depId)) continue;
          if (!out.some((r) => r.proposal_id === depId)) {
            out.push({ proposal_id: depId, action: 'reject' });
          }
        }
      }
    }
    return out;
  };

  const handleApply = async () => {
    const resolutions = buildResolutions();
    if (!resolutions.some((r) => r.action === 'approve')) return;
    setApplying(true);
    setApplyError(null);
    try {
      const res = await BackendAPI.applyEnhance(parseHash, resolutions);
      onResult(res.estimate);
      setAppliedIds((prev) => {
        const next = new Set(prev);
        res.applied.forEach((id) => next.add(id));
        return next;
      });
      // Remember which MODIFY candidate each applied swap used before triage is
      // cleared, so the collapsed card + memo can show the real swap.
      setAppliedChoices((prev) => {
        const next = { ...prev };
        for (const id of res.applied) {
          const p = proposals.find((x) => x.proposal_id === id);
          if (p?.kind === 'MODIFY' && p.modify) {
            next[id] = triage[id]?.choiceIndex ?? p.modify.default_index;
          }
        }
        return next;
      });
      setTriage((prev) => {
        const next = { ...prev };
        res.applied.forEach((id) => delete next[id]);
        return next;
      });
      setApplyResult({ applied: res.applied.length, skipped: res.skipped });
      onSessionChanged?.();
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const handleRevert = async (proposalId: string) => {
    setApplyError(null);
    try {
      const res = await BackendAPI.revertEnhance(parseHash, [proposalId]);
      onResult(res.estimate);
      setAppliedIds((prev) => {
        const next = new Set(prev);
        res.reverted.forEach((id) => next.delete(id));
        return next;
      });
      onSessionChanged?.();
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : 'Revert failed.');
    }
  };

  // The MODIFY candidate index an applied/rendered proposal resolves to.
  const choiceFor = useCallback(
    (p: EnhanceProposal) =>
      appliedChoices[p.proposal_id] ??
      triage[p.proposal_id]?.choiceIndex ??
      p.modify?.default_index ??
      0,
    [appliedChoices, triage],
  );

  // Serialize every applied proposal into a paste-ready supplement memo and copy
  // it to the clipboard (house pattern: label flips to "Copied" for 2s).
  const handleCopyMemo = () => {
    const text = buildSupplementMemo(appliedProposals, choiceFor);
    void navigator.clipboard?.writeText(text).then(() => {
      setMemoCopied(true);
      setTimeout(() => setMemoCopied(false), 2000);
    });
  };

  const groupLabel = (key: string) => (key === HOUSE_KEY ? 'House-wide / overall' : key);

  return (
    <div className="space-y-3" data-testid="enhance-panel">
      {/* narrative + loss context */}
      <Card className="p-[14px]">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-[13px] font-[580] text-[var(--tx-hi)]">Loss context</div>
          <SaveIndicator state={saveState} />
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="flex items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
            <Switch
              checked={context.cat3_confirmed}
              onCheckedChange={(v) => updateContext({ cat3_confirmed: v === true })}
              aria-label="Category 3 water (storm/flood)"
              data-testid="enhance-cat3"
            />
            Category 3 water (storm/flood)
          </label>

          <label className="flex items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
            Claim type
            <Select
              aria-label="Claim type"
              data-testid="enhance-claim-type"
              value={context.claim_type ?? 'auto'}
              onChange={(e) =>
                updateContext({
                  claim_type: e.target.value === 'auto' ? null : e.target.value,
                })
              }
              className="h-8 w-[150px]"
            >
              <option value="auto">Auto-detect</option>
              <option value="homeowner">Homeowner</option>
              <option value="flood">Flood / NFIP</option>
              <option value="commercial">Commercial</option>
            </Select>
          </label>

          <label className="flex items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
            Jurisdiction
            <Input
              aria-label="Jurisdiction"
              data-testid="enhance-jurisdiction"
              value={context.jurisdiction ?? ''}
              onChange={(e) => updateContext({ jurisdiction: e.target.value || null })}
              placeholder="e.g. Hillsborough County, FL"
              className="h-8 w-[220px]"
            />
          </label>
        </div>

        {/* narratives */}
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <label
              htmlFor="enhance-overall"
              className="text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-mid)]"
            >
              Overall narrative
            </label>
            <span className="text-[10.5px] text-[var(--tx-faint)]">
              {llmOffError
                ? 'Saved with this import — AI interpretation is currently unavailable'
                : 'Interpreted when you run enhancement'}
            </span>
          </div>
          <Textarea
            id="enhance-overall"
            data-testid="enhance-overall"
            value={narratives.overall}
            onChange={(e) => updateOverall(e.target.value)}
            rows={3}
            placeholder="Estimate-wide context: materials, grades, conditions, additional affected areas…"
          />
        </div>

        {rooms.length > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-mid)]">
              Per-room narrative
            </div>
            <div className="space-y-1.5">
              {rooms.map((room) => {
                const open = openRooms[room] ?? false;
                const val = narratives.rooms[room] ?? '';
                return (
                  <div
                    key={room}
                    className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-1)]"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenRooms((p) => ({ ...p, [room]: !open }))}
                      aria-expanded={open}
                      data-testid={`enhance-room-toggle-${room}`}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
                    >
                      <span className="text-[12.5px] text-[var(--tx-hi)]">{room}</span>
                      <span className="flex items-center gap-2">
                        {val.trim() && (
                          <span className="h-[6px] w-[6px] rounded-full bg-[var(--accent)]" />
                        )}
                        <svg
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                          className={`h-[11px] w-[11px] text-[var(--tx-faint)] transition-transform ${
                            open ? 'rotate-90' : ''
                          }`}
                        >
                          <path
                            d="M6 4l4 4-4 4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                    {open && (
                      <div className="px-3 pb-3">
                        <Textarea
                          aria-label={`${room} narrative`}
                          data-testid={`enhance-room-${room}`}
                          value={val}
                          onChange={(e) => updateRoomNarrative(room, e.target.value)}
                          rows={2}
                          placeholder={`What's different about ${room}?`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleRun(hasNarrative ? 'full' : 'deterministic')}
            disabled={running}
            data-testid="enhance-run"
          >
            <IconSpark />
            {running ? 'Running…' : 'Run enhancement'}
          </Button>
          {hasNarrative && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRun('deterministic')}
              disabled={running}
              data-testid="enhance-run-deterministic"
            >
              Run without narratives
            </Button>
          )}
          <span className="text-[11.5px] text-[var(--tx-lo)]">
            {hasNarrative
              ? 'Interprets your narratives with AI, then proposes additions for you to approve.'
              : 'Deterministic — no AI, no credit. Proposes additions for you to approve.'}
          </span>
        </div>
      </Card>

      {/* run-level messaging */}
      {llmOffError && (
        <Banner tone="warn" icon={<IconWarn />}>
          <div data-testid="enhance-llm-off">
            <div>{llmOffError}</div>
            <div className="mt-2">
              <Button
                size="sm"
                onClick={() => handleRun('deterministic')}
                disabled={running}
                data-testid="enhance-run-deterministic-fallback"
              >
                Run without narratives
              </Button>
            </div>
          </div>
        </Banner>
      )}
      {runError && (
        <Banner tone="warn" icon={<IconWarn />}>
          {runError}
        </Banner>
      )}
      {run?.degraded && (
        <Banner tone="warn" icon={<IconWarn />}>
          <span data-testid="enhance-degraded">
            Narratives were NOT interpreted (AI unavailable). Showing
            deterministic proposals only.
          </span>
        </Banner>
      )}
      {run && run.warnings.length > 0 && (
        <Banner tone="info" icon={<IconWarn />}>
          <ul className="list-disc space-y-0.5 pl-4">
            {run.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Banner>
      )}
      {applyError && (
        <Banner tone="warn" icon={<IconWarn />}>
          {applyError}
        </Banner>
      )}
      {applyResult && (
        <Banner tone="ok" icon={<IconCheck />}>
          Applied {applyResult.applied}{' '}
          {applyResult.applied === 1 ? 'addition' : 'additions'} to the estimate.
          {applyResult.skipped.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11.5px]">
              {applyResult.skipped.map((s) => (
                <li key={s.proposal_id}>Skipped: {s.reason}</li>
              ))}
            </ul>
          )}
        </Banner>
      )}

      {/* proposal queue */}
      {run == null ? (
        <Card className="p-0">
          <EmptyState
            icon={<IconSpark />}
            title="No enhancement run yet"
            description="Run the enhancement to see proposed additions you can approve."
          />
        </Card>
      ) : proposals.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={<IconCheck />}
            title="No additions proposed"
            description="Templates found nothing missing for this estimate."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {/* results header — copy the supplement justification for the memo */}
          <div className="flex items-center justify-between gap-2" data-testid="enhance-results-header">
            <span className="text-[11px] font-bold uppercase tracking-[.05em] text-[var(--tx-mid)]">
              Proposed additions
            </span>
            <Button
              size="sm"
              data-testid="enhance-copy-memo"
              onClick={handleCopyMemo}
              disabled={appliedProposals.length === 0}
              title={
                appliedProposals.length === 0
                  ? 'Apply at least one proposal to build the justification memo'
                  : undefined
              }
            >
              {memoCopied ? <IconCheck /> : <IconCopy />}
              {memoCopied ? 'Copied' : 'Copy supplement justification'}
            </Button>
          </div>

          <div className="space-y-4">
            {groups.map(([key, items]) => (
              <div key={key}>
                <div className="mb-1.5 flex items-center gap-[10px]">
                  <span className="text-[11px] font-bold uppercase tracking-[.05em] text-[var(--tx-mid)]">
                    {groupLabel(key)}
                  </span>
                  <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
                  <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)]">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
                <div className="space-y-2">
                  {items.map((p) => (
                    <ProposalCard
                      key={p.proposal_id}
                      proposal={p}
                      rooms={rooms}
                      applied={appliedIds.has(p.proposal_id)}
                      state={triage[p.proposal_id] ?? { choiceIndex: appliedChoices[p.proposal_id] }}
                      answer={answers[p.proposal_id]}
                      onChange={(patch) => setProposalState(p.proposal_id, patch)}
                      onAnswer={(a) => setAnswer(p.proposal_id, a)}
                      onRevert={() => handleRevert(p.proposal_id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* narrative fragments the interpreter couldn't turn into proposals */}
      {run && (run.uninterpreted?.length > 0 || run.failed_rooms?.length > 0) && (
        <div className="space-y-2" data-testid="enhance-uninterpreted">
          <div className="text-[11px] font-bold uppercase tracking-[.05em] text-[var(--tx-mid)]">
            Not interpreted — rephrase and re-run
          </div>
          {(run.uninterpreted ?? []).map((u, i) => (
            <div
              key={`u${i}`}
              className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-1)] px-3 py-2 opacity-80"
            >
              <div className="text-[11px] font-[560] text-[var(--tx-mid)]">
                {u.room ?? 'Overall'}
              </div>
              <div className="mt-0.5 text-[12px] italic text-[var(--tx-hi)]">
                &ldquo;{u.text}&rdquo;
              </div>
              <div className="mt-0.5 text-[11px] text-[var(--tx-lo)]">{u.reason}</div>
            </div>
          ))}
          {(run.failed_rooms ?? []).map((f, i) => (
            <div
              key={`f${i}`}
              className="rounded-[var(--r-md)] border border-[color-mix(in_oklab,var(--warn)_28%,transparent)] bg-[var(--warn-bg)] px-3 py-2"
            >
              <div className="text-[11px] font-[560] text-[var(--tx-mid)]">{f.room}</div>
              <div className="mt-0.5 text-[11px] text-[var(--warn)]">
                Couldn&rsquo;t process: {f.error}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* sticky triage footer — only when there's something to act on */}
      {actionable.length > 0 && (
        <ApplyBar
          approved={approvedCount}
          rejected={rejectedCount}
          open={openCount}
          requiredCount={requiredApprovable.length}
          onApproveAllRequired={approveAllRequired}
          onApply={handleApply}
          applying={applying}
          canApply={approvedCount > 0}
        />
      )}
    </div>
  );
}

/** Subtle "Saved" indicator for the debounced autosave. */
function SaveIndicator({ state }: { state: 'idle' | 'saving' | 'saved' }) {
  if (state === 'idle') return null;
  return (
    <span
      className="text-[11px] text-[var(--tx-faint)]"
      data-testid="enhance-save-state"
      aria-live="polite"
    >
      {state === 'saving' ? 'Saving…' : 'Saved'}
    </span>
  );
}

export default EnhancePanel;
